/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

import ConnectFormContainer from '../Containers/ConnectFormContainer';
import GoogleButton from '../Buttons/GoogleButton';
import MainLogo from '../Titles/MainLogo';
import OutlineButton from '../Buttons/OutlineButton';
import SmallLink from '../Links/SmallLink';
import SmallText from '../Texts/SmallText';
import Subtitle from '../Texts/Subtitle';
import TextInput from '../Inputs/TextInput';
import WhiteDivider from '../Components/WhiteDivider';
import { getApiUrl } from '../../config/api';
import { logAuth } from '../../utils/authDebug';
import { mapApiErrorsToFields } from '../../utils/mapApiErrors';
import { translateApiError } from '../../utils/translateApiError';
import { redirectAfterAuth } from '../../utils/postAuthRedirect';
import { buildGoogleOAuthUrl } from '../../utils/googleOAuthUrl';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  [key: string]: string | null;
}

const StyledCheckbox = styled(Checkbox)(({ theme }: any) => ({
  color: theme.palette.primary.main,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

type LoginFormProps = {
  onAuthenticated?: () => Promise<void>;
};

const LoginForm: React.FC<LoginFormProps> = ({ onAuthenticated }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const readFormValues = (): FormData => {
    const form = formRef.current;
    if (!form) return formData;
    const fd = new FormData(form);
    return {
      email: String(fd.get('email') ?? formData.email).trim(),
      password: String(fd.get('password') ?? formData.password),
      rememberMe: fd.get('rememberMe') === 'on' || formData.rememberMe,
    };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value } as any);
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const credentials = readFormValues();
    setIsSubmitting(true);
    try {
      setErrors({});
      const res = await fetch(getApiUrl('/api/v1/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(credentials),
      });
      const data = await res.json().catch(() => ({}));
      logAuth('login response', {
        ok: res.ok,
        status: res.status,
        success: data?.success,
        hasJwtoken: !!data?.jwtoken,
        origin: window.location.origin,
      });
      if (!res.ok || !data?.success) {
        if (data?.errors) {
          const fieldErrors = mapApiErrorsToFields(data.errors);
          if (fieldErrors.activation) {
            fieldErrors.email = fieldErrors.activation;
            delete fieldErrors.activation;
          }
          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
          }
        }
        setErrors({
          password: translateApiError(t, {
            message: data?.message,
            code: data?.code,
            status: res.status,
          }),
        });
        return;
      }

      // After login, return to the requested surface (same as post-verification).
      if (onAuthenticated) {
        await onAuthenticated();
      } else {
        await redirectAfterAuth(navigate);
      }
    } catch (err: any) {
      logAuth('login failed', { message: err?.message });
      setErrors((prev) => ({
        ...prev,
        password: translateApiError(t, { message: err?.message }),
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRegister = () => {
    window.location.href = buildGoogleOAuthUrl();
  };

  return (
    <ConnectFormContainer>
      <Box sx={{ position: 'relative' }}>
        {isSubmitting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '15px',
            }}
            aria-busy="true"
            aria-label={t('loading')}
          >
            <CircularProgress color="primary" size={48} />
          </Box>
        )}
      <MainLogo />
      <Subtitle>{t('auth.login')}</Subtitle>
      <form ref={formRef} onSubmit={handleSubmit} autoComplete="on">
        <TextInput
          label={t('auth.email')}
          name="email"
          type="email"
          autoComplete="username"
          fullWidth
          margin="normal"
          value={formData.email}
          onChange={handleChange}
          required
          error={Boolean(errors.email)}
          helperText={errors.email || undefined}
          style={{ borderColor: errors.email ? 'red' : undefined }}
          slotProps={{ htmlInput: { enterKeyHint: 'next' } }}
        />
        <TextInput
          label={t('auth.password')}
          type="password"
          name="password"
          autoComplete="current-password"
          fullWidth
          margin="normal"
          value={formData.password}
          onChange={handleChange}
          required
          error={Boolean(errors.password)}
          helperText={errors.password || undefined}
          style={{ borderColor: errors.password ? 'red' : undefined }}
          showPasswordToggle
          showPasswordLabel={t('auth.showPassword')}
          hidePasswordLabel={t('auth.hidePassword')}
          slotProps={{ htmlInput: { enterKeyHint: 'go' } }}
        />
        <FormControlLabel
          control={<StyledCheckbox name="rememberMe" checked={formData.rememberMe} onChange={handleChange} />}
          label={t('auth.rememberMe')}
          sx={{ '& .MuiFormControlLabel-label': { color: (theme) => theme.palette.primary.main } }}
        />
        <Box textAlign="center">
          <SmallLink to="/reset">{t('auth.forgotPassword')}</SmallLink>
        </Box>
        <OutlineButton type="submit" disabled={isSubmitting}>
          {t('auth.login')}
        </OutlineButton>
        <WhiteDivider>{t('or')}</WhiteDivider>
        <GoogleButton type="button" onClick={handleGoogleRegister}>{t('auth.loginWithGoogle')}</GoogleButton>
        <Box textAlign="center">
          <SmallText>{t('auth.notRegistered')}</SmallText>
          <SmallLink to="/register">{t('auth.register')}</SmallLink>
        </Box>
      </form>
      </Box>
    </ConnectFormContainer>
  );
};

export default LoginForm;

